<template>
  <div class="ll-page">

    <div class="ll-wrap">

      <!-- ===================================================== -->
      <!-- LOADING OVERLAY                                       -->
      <!-- ===================================================== -->
      <transition name="ll-fade">
        <div v-if="apiLoading" class="ll-loading-overlay">
          <div class="ll-loading-card">
            <div class="ll-logo ll-logo--lg">FM</div>
            <div class="ll-loading-text">{{ loadingMessage }}</div>
            <div class="ll-loading-bar">
              <div class="ll-loading-fill" :style="{ width: loadingProgress + '%' }"></div>
            </div>
            <div class="ll-loading-tip">{{ currentTip }}</div>
          </div>
        </div>
      </transition>

      <!-- ===================================================== -->
      <!-- HERO HEADER                                           -->
      <!-- ===================================================== -->
      <header class="ll-hero">
        <div class="ll-hero-grid">
          <div class="ll-hero-main">
            <div class="ll-hero-label">Layaway program</div>
            <div class="ll-hero-title">
              Layaway sales
              <transition name="ll-pop">
                <span v-if="filteredSalesCount > 0" class="ll-count-pill">{{ filteredSalesCount }}</span>
              </transition>
            </div>
            <div class="ll-hero-tags">
              <span class="ll-tag is-orange">
                <b-icon icon="cash-stack"></b-icon> ${{ formatCurrency(total()) }} paid
              </span>
              <span v-if="picked_location_id" class="ll-tag is-teal">
                <b-icon icon="geo-alt-fill"></b-icon> {{ getLocationLabel(picked_location_id) }}
              </span>
              <span class="ll-tag">
                <b-icon icon="calendar-event"></b-icon> {{ dateRangeLabel }}
              </span>
              <span v-if="picked_payment_method" class="ll-tag is-teal">
                <b-icon icon="credit-card"></b-icon> {{ paymentMethodLabel(picked_payment_method) }}
              </span>
            </div>
          </div>
          <div class="ll-hero-qr">
            <b-icon icon="hourglass-split" font-scale="2.6"></b-icon>
            <div>
              <div class="ll-qr-title">Layaway overview</div>
              <div class="ll-qr-sub">
                {{ sales.length }} total · {{ openCount }} open
              </div>
            </div>
          </div>
        </div>

        <div class="ll-hero-actions">
          <b-button class="ll-btn-ghost-dark" @click="toggleFilters">
            <b-icon icon="funnel-fill"></b-icon>
            {{ filtersVisible ? 'Hide filters' : 'Show filters' }}
          </b-button>
          <b-button class="ll-btn-ghost-dark" @click="fetch_init" :disabled="sales_being_loaded">
            <b-icon icon="arrow-clockwise"></b-icon> Refresh
          </b-button>
          <download-csv
            v-if="sales.length > 0 && ['admin'].includes(packet.userDetails.userrole)"
            class="btn ll-btn-ghost-dark ll-download-btn"
            :data="get_csv_data()"
            :name="get_csv_filename()"
          >
            <b-icon icon="download"></b-icon> Excel
          </download-csv>
        </div>
      </header>

      <!-- ===================================================== -->
      <!-- SINGLE-ROW FILTER STRIP                               -->
      <!-- ===================================================== -->
      <transition name="ll-fade-up">
        <section v-show="!isMobile || filtersVisible" class="ll-card ll-filterstrip">

          <div class="ll-filters-bar">

            <!-- Date range -->
            <div class="ll-fb-item ll-fb-dates">
              <b-input-group class="ll-fb-inputgroup">
                <b-input-group-prepend>
                  <b-input-group-text><b-icon icon="calendar-range"></b-icon></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  id="layawaydates"
                  :placeholder="[query.startDate, query.endDate].filter(Boolean).join(' → ') || 'Dates'"
                  class="ll-fb-input"
                  autocomplete="off"
                ></b-form-input>
                <b-input-group-append>
                  <b-button class="ll-btn-primary ll-fb-btn" @click="search_dates">Go</b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <!-- Invoice / customer search -->
            <div class="ll-fb-item ll-fb-search">
              <b-input-group class="ll-fb-inputgroup">
                <b-input-group-prepend>
                  <b-input-group-text><b-icon icon="search"></b-icon></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  v-model="query.invoice_id"
                  placeholder="Invoice #, name, email or phone…"
                  class="ll-fb-input"
                  @keyup.enter="search_invoice_id"
                  autocomplete="off"
                ></b-form-input>
                <b-input-group-append>
                  <b-button class="ll-btn-primary ll-fb-btn" @click="search_invoice_id">Search</b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <!-- Location -->
            <div class="ll-fb-item ll-fb-pill">
              <b-icon icon="geo-alt" class="ll-fb-pill-icon"></b-icon>
              <b-form-select
                :options="getFilteredLocations()"
                v-model="picked_location_id"
                class="ll-fb-select"
              >
                <template #first>
                  <b-form-select-option :value="null">All locations</b-form-select-option>
                </template>
              </b-form-select>
            </div>

            <!-- Payment method -->
            <div class="ll-fb-item ll-fb-pill">
              <b-icon icon="credit-card" class="ll-fb-pill-icon"></b-icon>
              <b-form-select
                :options="filteredPaymentMethods"
                v-model="picked_payment_method"
                @change="onPaymentMethodChange"
                class="ll-fb-select"
              >
                <template #first>
                  <b-form-select-option :value="null">All payment methods</b-form-select-option>
                </template>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="ll-fb-item ll-fb-pill">
              <b-icon icon="circle-half" class="ll-fb-pill-icon"></b-icon>
              <b-form-select
                :options="combined_status_options"
                v-model="combined_status_filter"
                class="ll-fb-select"
              ></b-form-select>
            </div>

            <!-- Order type -->
            <div class="ll-fb-item ll-fb-pill">
              <b-icon icon="truck" class="ll-fb-pill-icon"></b-icon>
              <b-form-select
                :options="orderTypeOptions"
                v-model="orderTypeFilter"
                class="ll-fb-select"
              ></b-form-select>
            </div>

            <b-button class="ll-btn-ghost ll-fb-reset" @click="resetFilters" title="Reset filters">
              <b-icon icon="arrow-counterclockwise"></b-icon>
            </b-button>
          </div>
        </section>
      </transition>

      <!-- ===================================================== -->
      <!-- STATS STRIP                                           -->
      <!-- ===================================================== -->
      <section v-if="sales.length > 0" class="ll-stats">
        <div class="ll-stat is-total">
          <div class="ll-stat-icon"><b-icon icon="cash-stack"></b-icon></div>
          <div class="ll-stat-body">
            <div class="ll-stat-value">${{ formatCurrency(total()) }}</div>
            <div class="ll-stat-label">Total paid</div>
          </div>
        </div>
        <div class="ll-stat is-count">
          <div class="ll-stat-icon"><b-icon icon="receipt"></b-icon></div>
          <div class="ll-stat-body">
            <div class="ll-stat-value">{{ filteredSalesCount }}</div>
            <div class="ll-stat-label">Layaways shown</div>
          </div>
        </div>
        <div class="ll-stat is-open">
          <div class="ll-stat-icon"><b-icon icon="hourglass-split"></b-icon></div>
          <div class="ll-stat-body">
            <div class="ll-stat-value">{{ openCount }}</div>
            <div class="ll-stat-label">Still open</div>
          </div>
        </div>
        <div class="ll-stat is-delivered">
          <div class="ll-stat-icon"><b-icon icon="check2-circle"></b-icon></div>
          <div class="ll-stat-body">
            <div class="ll-stat-value">{{ deliveredCount }}</div>
            <div class="ll-stat-label">Delivered</div>
          </div>
        </div>
      </section>

      <!-- ===================================================== -->
      <!-- TABLE — 7 columns                                     -->
      <!-- ===================================================== -->
      <div v-if="sales_being_loaded && !apiLoading" class="ll-card ll-loading-inline">
        <b-spinner variant="primary"></b-spinner>
        <span>Loading layaway sales…</span>
      </div>

      <section v-else-if="sales.length > 0" class="ll-card ll-table-card">
        <div class="ll-table-scroll">
          <table class="ll-table">
            <thead>
              <tr>
                <th class="ll-th" style="min-width: 150px" @click="sortby('invoice_id')">
                  <span>Invoice</span> <span class="ll-sort-icon">↕</span>
                </th>
                <th class="ll-th" style="min-width: 190px" @click="sortby('customer')">
                  <span>Customer</span> <span class="ll-sort-icon">↕</span>
                </th>
                <th class="ll-th" style="min-width: 190px" @click="sortby('location_id')">
                  <span>Location &amp; agent</span> <span class="ll-sort-icon">↕</span>
                </th>
                <th class="ll-th" style="min-width: 190px">Order &amp; delivery</th>
                <th class="ll-th" style="min-width: 190px">Payment</th>
                <th class="ll-th" style="min-width: 140px" @click="sortby('status_display')">
                  <span>Status</span> <span class="ll-sort-icon">↕</span>
                </th>
                <th class="ll-th ll-th--actions" style="min-width: 200px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <!-- Totals row -->
              <tr class="ll-totals-row">
                <td><span class="ll-totals-label">Gross</span></td>
                <td></td>
                <td></td>
                <td></td>
                <td class="ll-totals-amt">
                  <b-icon icon="cash-stack"></b-icon>
                  <span>${{ formatCurrency(total()) }}</span>
                </td>
                <td></td>
                <td></td>
              </tr>

              <!-- Data rows -->
              <tr
                v-for="(sale, idx) in sales"
                v-if="allowed(sale) && row_part_of_chunk(idx)"
                :key="'layaway-' + idx + '-' + sale.invoice_id"
                class="ll-row"
                :class="{ 'is-highlight': isPaymentMethodMatch(sale) }"
                :style="{ animationDelay: (idx * 0.015) + 's' }"
              >
                <!-- Invoice -->
                <td class="ll-td">
                  <div class="ll-invoice-cell is-layaway">
                    <div class="ll-invoice-id">{{ (sale.invoice_id || '').toUpperCase() }}</div>
                    <div class="ll-invoice-date">{{ sale.datetime }}</div>
                    <div class="ll-invoice-tags">
                      <span class="ll-mini-chip is-warn">
                        <b-icon icon="hourglass-split"></b-icon> LAYAWAY
                      </span>
                      <span v-if="sale.total_payments > 1" class="ll-mini-chip">
                        {{ sale.payment_index + 1 }}/{{ sale.total_payments }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- Customer -->
                <td class="ll-td">
                  <div class="ll-cust-cell">
                    <div class="ll-cust-name">{{ (sale.customer_fullname || 'Walk-in').toUpperCase() }}</div>
                    <div v-if="sale.customer_phone" class="ll-cust-line">
                      <b-icon icon="telephone"></b-icon> {{ sale.customer_phone }}
                    </div>
                    <div v-if="sale.email" class="ll-cust-line is-muted">
                      <b-icon icon="envelope"></b-icon> {{ sale.email }}
                    </div>
                  </div>
                </td>

                <!-- Location & agent -->
                <td class="ll-td">
                  <div class="ll-loc-cell">
                    <div class="ll-loc-primary">
                      <b-icon icon="geo-alt-fill"></b-icon>
                      {{ (packet.locationsdict && packet.locationsdict[sale.location_id]) || '—' }}
                    </div>
                    <div v-if="packet.sublocationsdict && packet.sublocationsdict[sale.sublocation_id]" class="ll-loc-secondary">
                      {{ packet.sublocationsdict[sale.sublocation_id] }}
                    </div>
                    <div class="ll-agent-chip">
                      <b-icon icon="person-badge"></b-icon>
                      {{ (sale.username || '—').toUpperCase() }}
                    </div>
                  </div>
                </td>

                <!-- Order & delivery -->
                <td class="ll-td">
                  <div class="ll-od-cell">
                    <div v-if="getUniqueOrderTypes(sale).length" class="ll-order-chips">
                      <span
                        v-for="ot in getOrderTypesWithCount(sale).slice(0, 2)"
                        :key="ot.type"
                        class="ll-mini-chip"
                        :class="orderTypeChipClass(ot.type)"
                      >
                        <b-icon :icon="orderTypeIcon(ot.type)"></b-icon>
                        {{ shortOrderType(ot.type) }} · {{ ot.count }}
                      </span>
                      <b-button
                        v-if="getUniqueOrderTypes(sale).length > 2"
                        size="sm"
                        class="ll-mini-btn is-tiny"
                        @click="showOrderTypes(sale)"
                      >
                        +{{ getUniqueOrderTypes(sale).length - 2 }}
                      </b-button>
                    </div>
                    <div v-else class="ll-muted-small">No order types</div>

                    <div class="ll-deliv-line">
                      <template v-if="sale.delivery_dates && sale.delivery_dates.length > 0">
                        <b-button
                          v-if="sale.delivery_dates.length > 1"
                          size="sm"
                          class="ll-mini-btn is-tiny is-teal"
                          @click="showDeliveryDates(sale)"
                        >
                          <b-icon icon="calendar-week"></b-icon>
                          {{ sale.delivery_dates.length }} dates
                        </b-button>
                        <span v-else class="ll-mini-chip is-warn">
                          <b-icon icon="calendar-event"></b-icon> {{ sale.delivery_dates[0] }}
                        </span>
                      </template>
                      <span v-else class="ll-muted-small">No dates</span>
                    </div>
                  </div>
                </td>

                <!-- Payment -->
                <td class="ll-td">
                  <div
                    class="ll-pay-cell"
                    @click="showPaymentHistory(sale)"
                    :title="'View payment history for ' + sale.invoice_id"
                  >
                    <div class="ll-pay-amt is-teal">
                      ${{ formatCurrency(sale.payment_amount || sale.paid_so_far || 0) }}
                    </div>
                    <div v-if="sale.grand_total" class="ll-pay-note">
                      Total ${{ formatCurrency(sale.grand_total) }}
                    </div>
                    <div v-if="sale.payment_methods && sale.payment_methods.length > 0" class="ll-methods-cell">
                      <span
                        v-for="(method, index) in getPaymentMethodsDisplay(sale.payment_methods).slice(0, 3)"
                        :key="index"
                        class="ll-mini-chip"
                        :class="getPaymentMethodChipClass(method)"
                      >
                        {{ method }}
                      </span>
                      <span
                        v-if="getPaymentMethodsDisplay(sale.payment_methods).length > 3"
                        class="ll-mini-chip"
                      >
                        +{{ getPaymentMethodsDisplay(sale.payment_methods).length - 3 }}
                      </span>
                    </div>
                    <div v-else class="ll-muted-small">No methods</div>
                  </div>
                </td>

                <!-- Status -->
                <td class="ll-td">
                  <div class="ll-status-badge" :class="statusChipClass(sale)">
                    {{ getStatusDisplay(sale) }}
                    <span class="ll-status-sub">
                      {{ sale.completed ? 'COMPLETED' : 'IN PROGRESS' }}
                    </span>
                  </div>
                </td>

                <!-- Actions -->
                <td class="ll-td ll-td--actions">
                  <div class="ll-actions">
                    <b-button class="ll-mini-btn is-primary" size="sm" @click="navigateToInvoice(idx)">
                      <b-icon icon="box-arrow-up-right"></b-icon> View
                    </b-button>

                    <b-button
                      v-if="!isArchived(sale) && ['admin'].includes(packet.userDetails.userrole)"
                      class="ll-mini-btn"
                      size="sm"
                      @click="archive_invoice(sale.invoice_id)"
                    >
                      <b-icon icon="archive"></b-icon>
                    </b-button>

                    <b-button
                      v-if="isArchived(sale) && ['admin', 'locationadmin', 'locationmanager'].includes(packet.userDetails.userrole)"
                      class="ll-mini-btn is-teal"
                      size="sm"
                      @click="unarchive_invoice(sale.invoice_id)"
                    >
                      <b-icon icon="arrow-counterclockwise"></b-icon>
                    </b-button>

                    <b-button
                      v-if="isArchived(sale) && packet.userDetails.userrole === 'admin'"
                      class="ll-mini-btn is-danger"
                      size="sm"
                      @click="void_invoice(sale.invoice_id)"
                    >
                      <b-icon icon="trash"></b-icon>
                    </b-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Empty state -->
      <div v-else-if="!sales_being_loaded" class="ll-card ll-empty">
        <div class="ll-empty-icon"><b-icon icon="hourglass-split" font-scale="2.4"></b-icon></div>
        <h3>No layaway sales found</h3>
        <p>Try adjusting the date range or filters to see results.</p>
        <b-button class="ll-btn-primary" @click="resetFilters">
          <b-icon icon="arrow-counterclockwise"></b-icon> Reset filters
        </b-button>
      </div>

    </div>

    <!-- ===================================================== -->
    <!-- DELIVERY DATES MODAL                                  -->
    <!-- ===================================================== -->
    <b-modal id="delivery-dates-modal" size="md" hide-footer centered content-class="ll-modal">
      <template #modal-header>
        <div class="ll-modal-title">
          <span class="ll-modal-title-icon"><b-icon icon="calendar-week"></b-icon></span>
          <div>
            <div class="ll-modal-title-main">Delivery dates</div>
            <div class="ll-modal-title-sub" v-if="selectedSale">{{ selectedSale.invoice_id }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedSale" class="ll-modal-body">
        <div class="ll-modal-summary">
          <div>
            <strong>{{ selectedSale.customer_fullname }}</strong>
            <small>{{ selectedSale.customer_phone }}</small>
          </div>
          <div class="ll-chip is-warn">
            {{ selectedSale.delivery_dates.length }} dates
          </div>
        </div>

        <ul class="ll-date-list">
          <li
            v-for="(date, index) in selectedSale.delivery_dates"
            :key="index"
            class="ll-date-item"
            :class="{ 'is-upcoming': isUpcomingDate(date) }"
          >
            <span class="ll-date-icon"><b-icon icon="truck"></b-icon></span>
            <div class="ll-date-details">
              <div class="ll-date-display">{{ formatDeliveryDate(date) }}</div>
              <div class="ll-date-status" :class="getDateStatusClass(date)">{{ getDateStatus(date) }}</div>
            </div>
            <span class="ll-chip">#{{ index + 1 }}</span>
          </li>
        </ul>

        <div class="ll-modal-foot">
          <span class="ll-foot-note">
            Upcoming: <b>{{ getUpcomingDatesCount(selectedSale.delivery_dates) }}</b> of
            {{ selectedSale.delivery_dates.length }}
          </span>
          <b-button class="ll-btn-ghost" @click="$bvModal.hide('delivery-dates-modal')">Close</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===================================================== -->
    <!-- ORDER TYPES MODAL                                     -->
    <!-- ===================================================== -->
    <b-modal id="order-types-modal" size="md" hide-footer centered content-class="ll-modal">
      <template #modal-header>
        <div class="ll-modal-title">
          <span class="ll-modal-title-icon"><b-icon icon="box-seam"></b-icon></span>
          <div>
            <div class="ll-modal-title-main">Order types</div>
            <div class="ll-modal-title-sub" v-if="selectedSale">{{ selectedSale.invoice_id }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedSale" class="ll-modal-body">
        <div class="ll-modal-summary">
          <div>
            <strong>{{ selectedSale.customer_fullname }}</strong>
            <small>{{ selectedSale.customer_phone }}</small>
          </div>
          <div class="ll-chip is-teal">{{ selectedSale.items.length }} items</div>
        </div>

        <ul class="ll-order-list">
          <li
            v-for="(ot, index) in getOrderTypesWithCount(selectedSale)"
            :key="index"
            class="ll-order-item"
            :class="getOrderTypeItemClass(ot.type)"
          >
            <span class="ll-order-icon"><b-icon :icon="orderTypeIcon(ot.type)"></b-icon></span>
            <div class="ll-order-details">
              <div class="ll-order-display">{{ formatOrderType(ot.type) }}</div>
              <div class="ll-order-count">{{ ot.count }} item{{ ot.count > 1 ? 's' : '' }}</div>
            </div>
            <span class="ll-chip">{{ getOrderTypeAbbreviation(ot.type) }}</span>
          </li>
        </ul>

        <div class="ll-modal-foot">
          <span class="ll-foot-note">
            {{ getUniqueOrderTypes(selectedSale).length }} type(s) ·
            {{ selectedSale.items.length }} items
          </span>
          <b-button class="ll-btn-ghost" @click="$bvModal.hide('order-types-modal')">Close</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===================================================== -->
    <!-- PAYMENT HISTORY MODAL                                 -->
    <!-- ===================================================== -->
    <b-modal id="payment-history-modal" size="lg" hide-footer centered content-class="ll-modal">
      <template #modal-header>
        <div class="ll-modal-title">
          <span class="ll-modal-title-icon"><b-icon icon="credit-card"></b-icon></span>
          <div>
            <div class="ll-modal-title-main">Payment history</div>
            <div class="ll-modal-title-sub" v-if="selectedInvoice">{{ selectedInvoice.invoice_id }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedInvoice" class="ll-modal-body">
        <div class="ll-pay-modal-head">
          <div class="ll-pay-modal-customer">
            <strong>{{ selectedInvoice.customer_fullname }}</strong>
            <small>
              <template v-if="selectedInvoice.customer_phone">{{ selectedInvoice.customer_phone }}</template>
              <template v-if="selectedInvoice.email"> · {{ selectedInvoice.email }}</template>
            </small>
            <div class="ll-pay-modal-tags">
              <span v-if="selectedInvoice.is_layaway" class="ll-chip is-warn">LAYAWAY</span>
            </div>
          </div>
          <div class="ll-pay-modal-totals">
            <div class="ll-pay-tot-row">
              <span>Grand total</span>
              <strong>${{ formatCurrency(selectedInvoice.grand_total) }}</strong>
            </div>
            <div class="ll-pay-tot-row is-teal">
              <span>Paid so far</span>
              <strong>${{ formatCurrency(getTotalPaid(selectedInvoice)) }}</strong>
            </div>
          </div>
        </div>

        <div v-if="selectedInvoice.is_layaway" class="ll-pay-progress">
          <div class="ll-pay-progress-head">
            <span>Payment progress</span>
            <strong>
              {{ Math.round((getTotalPaid(selectedInvoice) / selectedInvoice.grand_total) * 100) }}%
            </strong>
          </div>
          <b-progress
            height="8px"
            :value="getTotalPaid(selectedInvoice)"
            :max="selectedInvoice.grand_total"
            variant="success"
          ></b-progress>
          <div class="ll-pay-progress-foot">
            <span class="is-warn">
              Remaining: $
              {{ formatCurrency((selectedInvoice.grand_total || 0) - (getTotalPaid(selectedInvoice) || 0)) }}
            </span>
          </div>
        </div>

        <div class="ll-pay-section">
          <div class="ll-pay-section-head">
            <b-icon icon="receipt"></b-icon>
            Payment transactions
            <span class="ll-chip">
              {{ selectedInvoice.payment_details ? selectedInvoice.payment_details.length : 0 }}
            </span>
          </div>

          <ul
            v-if="selectedInvoice.payment_details && selectedInvoice.payment_details.length > 0"
            class="ll-pay-list"
          >
            <li
              v-for="(payment, idx) in selectedInvoice.payment_details"
              :key="idx"
              class="ll-pay-item"
              :class="{ 'is-recent': isRecentPayment(payment.ts) }"
            >
              <span class="ll-pay-icon" :class="'is-' + (payment.value || 'default')">
                {{ getPaymentMethodIcon(payment.value) }}
              </span>
              <div class="ll-pay-item-body">
                <div class="ll-pay-item-method">{{ getPaymentMethodDisplay(payment.value) }}</div>
                <div class="ll-pay-item-date">{{ formatPaymentDate(payment.ts) }}</div>
                <div v-if="payment.store_credit_invoice_id || payment.charged_tax_itself" class="ll-pay-item-tags">
                  <span v-if="payment.store_credit_invoice_id" class="ll-chip is-teal">Store credit</span>
                  <span v-if="payment.charged_tax_itself" class="ll-chip is-warn">Tax included</span>
                </div>
              </div>
              <div class="ll-pay-item-amount is-teal">
                ${{ formatCurrency(payment.amount) }}
              </div>
            </li>
          </ul>

          <div v-else class="ll-empty-inline">
            <b-icon icon="receipt" font-scale="1.6"></b-icon>
            <p>No payment transactions found</p>
          </div>
        </div>

        <div class="ll-modal-foot">
          <span class="ll-foot-note">Total paid: ${{ formatCurrency(getTotalPaid(selectedInvoice)) }}</span>
          <div class="ll-modal-foot-actions">
            <b-button class="ll-btn-ghost" @click="$bvModal.hide('payment-history-modal')">Close</b-button>
            <b-button class="ll-btn-primary" @click="navigateToSelectedInvoice">
              <b-icon icon="box-arrow-up-right"></b-icon> View full invoice
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import $ from 'jquery'
import Backer from '@/components/globals/Backer'
import Requestor from '@/utilities/Requestor'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'

import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'

import 'lightpick/css/lightpick.css'
import moment from 'moment'
import { BIconFunnelFill } from 'bootstrap-vue'

export default {
  name: "ListLayAwaySales",
  props: ["showbacker"],
  components: { Backer, VueGoodTable, Multiselect, Loading },
  data: function () {
    return {
      packet: {},
      sales: [],
      sales_being_loaded: false,
      query: {
        startDate: null,
        endDate: null,
        invoice_id: null,
      },
      selectedSale: null,
      selectedInvoice: null,
      picked_location_id: null,
      finance_companies: [],
      orderTypeFilter: "all",
      orderTypeOptions: [
        { value: "all", text: "All order types" },
        { value: "home_delivery", text: "Home delivery" },
        { value: "store_pickup", text: "Store pickup" },
      ],
      payment_methods: [
        { value: "CASH", text: "Cash involved" },
        { value: "CC", text: "Credit card involved" },
        { value: "STORE_CREDIT", text: "Store credit involved" },
        { value: "FINANCE", text: "Finance involved" },
        { value: "TAX_EXEMPT", text: "Tax exempt involved" },
      ],
      combined_status_filter: "all",
      combined_status_options: [
        { value: "all", text: "All statuses" },
        { value: "new", text: "New" },
        { value: "open", text: "Open" },
        { value: "processed", text: "Processed" },
        { value: "partial", text: "Partial" },
        { value: "backorder", text: "Backorder" },
        { value: "delivered", text: "Delivered" },
        { value: "completed", text: "Completed" },
        { value: "archived", text: "Archived" },
      ],
      status_filter: "all",
      status_options: [
        { value: "all", text: "All statuses" },
        { value: "0", text: "New" },
        { value: "1", text: "Open" },
        { value: "2", text: "Processed only" },
        { value: "3", text: "Backorder" },
        { value: "4", text: "Partial" },
        { value: "5", text: "Delivered" },
      ],
      completion_filter: "all",
      completion_options: [
        { value: "all", text: "All completion" },
        { value: "not_completed", text: "Not completed only" },
        { value: "completed", text: "Completed only" },
      ],
      sale_type_filter: "layaway",
      sale_type_options: [
        { value: "layaway", text: "Layaway sales only" },
      ],
      picked_payment_method: null,
      chunksize: 2000000,
      chunkoptions: [],
      chunk_allowed: null,
      invoice_type_options: [
        { value: "regular", text: "Regular" },
        { value: "layaway", text: "Layaway" },
      ],
      invoice_type: "layaway",
      sort_asc: true,
      filtersVisible: false,
      isMobile: false,
      apiLoading: false,
      loadingMessage: "Loading Layaway Sales",
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,
      tips: [
        "Loading layaway sales data…",
        "Fetching payment history…",
        "Processing layaway details…",
        "Calculating totals…",
        "Organizing layaway records…",
        "Updating payment status…",
      ],
    }
  },
  computed: {
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0];
    },
    filteredSalesCount() {
      return this.sales.filter((s) => this.allowed(s)).length;
    },
    openCount() {
      return this.sales.filter((s) => this.allowed(s) && !s.completed).length;
    },
    deliveredCount() {
      return this.sales.filter((s) => this.allowed(s) && s.completed).length;
    },
    dateRangeLabel() {
      if (this.query.startDate && this.query.endDate) {
        return `${moment(this.query.startDate).format("MMM DD")} → ${moment(this.query.endDate).format("MMM DD")}`;
      }
      if (this.query.invoice_id) return `Search: ${this.query.invoice_id}`;
      return "All time";
    },
    filteredPaymentMethods() {
      let availableMethods = [
        { value: "CASH", text: "Cash involved" },
        { value: "CC", text: "Credit card involved" },
        { value: "STORE_CREDIT", text: "Store credit involved" },
        { value: "FINANCE", text: "Finance involved" },
        { value: "TAX_EXEMPT", text: "Tax exempt involved" },
      ];

      const presentMethods = new Set();

      this.sales.forEach(sale => {
        if (sale.payment_methods && Array.isArray(sale.payment_methods)) {
          sale.payment_methods.forEach(payment => {
            if (payment && payment.value) {
              if (payment.value === "cash") presentMethods.add("CASH");
              if (payment.value === "cc") presentMethods.add("CC");
              if (payment.value === "store_credit") presentMethods.add("STORE_CREDIT");
              if (payment.value === "finance") presentMethods.add("FINANCE");
              if (payment.value === "tax_exempt") presentMethods.add("TAX_EXEMPT");

              if (payment.value === "finance" && payment.name && payment.name !== "N/A") {
                presentMethods.add("FINANCE - " + payment.name);
              }
              if (payment.value === "tax_exempt" && payment.name && payment.name !== "N/A") {
                presentMethods.add("TAX_EXEMPT - " + payment.name);
              }
            }
          });
        }
      });

      availableMethods = availableMethods.filter(method => presentMethods.has(method.value));

      this.finance_companies.forEach(fc => {
        const financeOptionValue = "FINANCE - " + fc.finance_company_name;
        if (presentMethods.has(financeOptionValue)) {
          availableMethods.push({
            value: financeOptionValue,
            text: "Finance · " + fc.finance_company_name,
          });
        }
        if (fc.company_type === "tax_exempt") {
          const taxExemptOptionValue = "TAX_EXEMPT - " + fc.finance_company_name;
          if (presentMethods.has(taxExemptOptionValue)) {
            availableMethods.push({
              value: taxExemptOptionValue,
              text: "Tax exempt · " + fc.finance_company_name,
            });
          }
        }
      });

      return availableMethods;
    },
  },
  methods: {
    /* ===========================
       LOADER
    =========================== */
    startApiLoading(message = "Loading Layaway Sales") {
      this.apiCallStack++;
      if (!this.apiLoading) this.startLoadingAnimation(message);
      else this.loadingMessage = message;
    },
    stopApiLoading() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0 && this.apiLoading) this.stopLoadingAnimation(true);
    },
    startLoadingAnimation(message = "Loading Layaway Sales") {
      this.apiLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;

      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);

      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) this.currentTipIndex++;
      }, 2000);

      let messages = ['Loading Layaway Sales', 'Fetching Data', 'Processing Transactions', 'Calculating Totals', 'Finalizing'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        }
      }, 1800);
    },
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      if (this.msgInterval) clearInterval(this.msgInterval);

      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => { this.apiLoading = false; }, 500);
      } else {
        this.apiLoading = false;
      }
    },

    /* ===========================
       UI HELPERS
    =========================== */
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },
    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },
    resetFilters() {
      this.picked_location_id = null;
      this.picked_payment_method = null;
      this.combined_status_filter = "all";
      this.orderTypeFilter = "all";
      this.query.startDate = null;
      this.query.endDate = null;
      this.query.invoice_id = null;
      this.fetch_init();
    },
    getLocationLabel(value) {
      if (!this.packet.locations) return value;
      const loc = this.packet.locations.find((l) => l.value == value);
      return loc ? loc.text : value;
    },
    paymentMethodLabel(value) {
      if (!value) return "";
      const opt = this.filteredPaymentMethods.find((m) => m.value === value);
      return opt ? opt.text : value;
    },
    orderTypeChipClass(type) {
      if (type === "home_delivery") return "is-teal";
      if (type === "store_pickup") return "is-warn";
      return "";
    },
    orderTypeIcon(type) {
      const map = {
        home_delivery: "house",
        store_pickup: "shop",
        curbside: "car-front",
        delivery: "truck",
        pickup: "bag",
      };
      return map[type] || "box";
    },
    shortOrderType(type) {
      const map = {
        home_delivery: "Delivery",
        store_pickup: "Pickup",
        curbside: "Curbside",
        delivery: "Delivery",
        pickup: "Pickup",
      };
      return map[type] || this.formatOrderType(type);
    },
    statusChipClass(sale) {
      const s = this.getStatusDisplay(sale);
      if (s === "COMPLETED" || s === "DELIVERED") return "is-teal";
      if (s === "REFUNDED") return "is-danger";
      if (s === "ARCHIVED") return "is-warn";
      if (s === "NEW") return "is-teal";
      if (s === "PROCESSED") return "is-teal";
      if (s === "PARTIAL" || s === "BACKORDER") return "is-warn";
      return "";
    },

    /* ===========================
       ORDER TYPES / DATES
    =========================== */
    formatOrderTypeDisplay(orderType) {
      const typeName = this.formatOrderType(orderType.type);
      return `${typeName} (${orderType.count} item${orderType.count > 1 ? "s" : ""})`;
    },
    isArchived(sale) {
      return sale.status === 10;
    },
    archive_invoice(invoice_id) {
      let that = this;
      let s = function (response) {
        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        else {
          that.$bvToast.toast("Invoice archived", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.fetch_init();
        }
      };
      let f = function () {
        that.$bvToast.toast("Error archiving invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };
      Requestor.postSyncP("/api/sales/archive_invoice/", { invoice_id }, this, s, f);
    },
    unarchive_invoice(invoice_id) {
      let that = this;
      let s = function (response) {
        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        else {
          that.$bvToast.toast("Invoice unarchived", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.fetch_init();
        }
      };
      let f = function () {
        that.$bvToast.toast("Error unarchiving invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };
      Requestor.postSyncP("/api/sales/unarchive_invoice/", { invoice_id }, this, s, f);
    },
    getUniqueOrderTypes(sale) {
      if (!sale.items || !Array.isArray(sale.items)) return [];
      const orderTypes = [...new Set(sale.items.map(item => item.order_type))];
      return orderTypes.filter(type => type);
    },
    getOrderTypesWithCount(sale) {
      if (!sale.items || !Array.isArray(sale.items)) return [];
      const typeCount = {};
      sale.items.forEach(item => {
        const type = item.order_type;
        if (type) typeCount[type] = (typeCount[type] || 0) + 1;
      });
      return Object.keys(typeCount).map(type => ({ type, count: typeCount[type] }));
    },
    showOrderTypes(sale) {
      this.selectedSale = { ...sale };
      this.$bvModal.show('order-types-modal');
    },
    formatOrderType(orderType) {
      if (!orderType) return 'Unknown';
      const typeMap = {
        'home_delivery': 'Home Delivery',
        'store_pickup': 'Store Pickup',
        'curbside': 'Curbside Pickup',
        'delivery': 'Delivery',
        'pickup': 'Pickup',
      };
      return typeMap[orderType] || orderType.replace(/_/g, ' ').toUpperCase();
    },
    getOrderTypeItemClass(orderType) {
      const classMap = {
        'home_delivery': 'is-home-delivery',
        'store_pickup': 'is-store-pickup',
        'curbside': 'is-curbside',
        'delivery': 'is-delivery',
        'pickup': 'is-pickup',
      };
      return classMap[orderType] || '';
    },
    getOrderTypeAbbreviation(orderType) {
      const abbrevMap = {
        'home_delivery': 'HD',
        'store_pickup': 'SP',
        'curbside': 'CRB',
        'delivery': 'DEL',
        'pickup': 'PUP',
      };
      return abbrevMap[orderType] || 'OT';
    },
    showDeliveryDates(sale) {
      this.selectedSale = { ...sale };
      this.$bvModal.show('delivery-dates-modal');
    },
    formatDeliveryDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return moment(dateString).format('ddd, MMM DD, YYYY');
      } catch (e) { return dateString; }
    },
    isUpcomingDate(dateString) {
      if (!dateString) return false;
      try {
        const d = moment(dateString).startOf('day');
        const t = moment().startOf('day');
        return d.isSameOrAfter(t);
      } catch (e) { return false; }
    },
    getDateStatus(dateString) {
      if (!dateString) return 'Unknown';
      try {
        const d = moment(dateString).startOf('day');
        const t = moment().startOf('day');
        if (d.isBefore(t)) return 'Completed';
        if (d.isSame(t)) return 'Today';
        if (d.isSame(t.clone().add(1, 'day'))) return 'Tomorrow';
        return `In ${d.diff(t, 'days')} days`;
      } catch (e) { return 'Unknown'; }
    },
    getDateStatusClass(dateString) {
      if (!dateString) return 'is-muted';
      try {
        const d = moment(dateString).startOf('day');
        const t = moment().startOf('day');
        if (d.isBefore(t)) return 'is-ok';
        if (d.isSame(t)) return 'is-warn';
        return 'is-teal';
      } catch (e) { return 'is-muted'; }
    },
    getUpcomingDatesCount(dates) {
      if (!dates || !Array.isArray(dates)) return 0;
      return dates.filter(d => this.isUpcomingDate(d)).length;
    },

    /* ===========================
       STATUS
    =========================== */
    getStatusDisplay(sale) {
      if (sale.status === 10) return 'ARCHIVED';
      if (sale.transaction_type === 'REFUND') return 'REFUNDED';
      if (sale.status_display) return sale.status_display;
      if (sale.status === 0) return 'NEW';
      if (sale.status === 1) return 'PROCESSED';
      if (sale.completed) return 'DELIVERED';
      if (sale.delivered_items && sale.total_items) {
        if (sale.delivered_items === sale.total_items) return 'DELIVERED';
        if (sale.delivered_items > 0 && sale.delivered_items < sale.total_items) return 'PARTIAL';
      }
      if (sale.backorder_items && sale.backorder_items > 0) return 'BACKORDER';
      return 'OPEN';
    },

    /* ===========================
       NUMBERS / MONEY
    =========================== */
    safeToNumber(value, defaultValue = 0) {
      if (value === null || value === undefined) return defaultValue;
      const num = parseFloat(value);
      return isNaN(num) ? defaultValue : num;
    },
    formatCurrency(value) {
      return this.safeToNumber(value).toFixed(2);
    },
    getTotalPaid(invoice) {
      if (!invoice.payment_details || !Array.isArray(invoice.payment_details)) {
        return this.safeToNumber(invoice.paid_so_far);
      }
      return invoice.payment_details.reduce((total, payment) => total + this.safeToNumber(payment.amount), 0);
    },

    /* ===========================
       PAYMENT METHOD / DISPLAY
    =========================== */
    debugPaymentMethods() {
      console.log("=== PAYMENT METHODS DEBUG ===");
      console.log("Selected payment method:", this.picked_payment_method);
      this.sales.forEach((sale, index) => {
        console.log("Sale " + index + " (" + sale.invoice_id + "):", {
          payment_methods: sale.payment_methods || [],
          payment_amount: sale.payment_amount,
          total_payments: sale.total_payments,
          payment_index: sale.payment_index,
        });
      });
    },
    onPaymentMethodChange() { this.debugPaymentMethods(); },
    isPaymentMethodMatch(sale) {
      if (!this.picked_payment_method) return false;
      var pms = sale.payment_methods || [];
      var picked_payment_method = this.picked_payment_method;
      return pms.some(function (payment) {
        if (!payment || !payment.value) return false;
        if (picked_payment_method === "CASH") return payment.value === "cash";
        if (picked_payment_method === "CC") return payment.value === "cc";
        if (picked_payment_method === "STORE_CREDIT") return payment.value === "store_credit";
        if (picked_payment_method === "FINANCE") return payment.value === "finance";
        if (picked_payment_method === "TAX_EXEMPT") return payment.value === "tax_exempt";
        if (picked_payment_method.startsWith("FINANCE - ")) {
          return payment.value === "finance" && payment.name === picked_payment_method.replace("FINANCE - ", "");
        }
        if (picked_payment_method.startsWith("TAX_EXEMPT - ")) {
          return payment.value === "tax_exempt" && payment.name === picked_payment_method.replace("TAX_EXEMPT - ", "");
        }
        return false;
      });
    },
    getPaymentMethodDisplay(method) {
      const map = {
        'cash': 'Cash',
        'cc': 'Credit card',
        'store_credit': 'Store credit',
        'finance': 'Finance',
        'tax_exempt': 'Tax exempt',
      };
      return map[method] || (method ? method.toUpperCase() : 'Unknown');
    },
    getPaymentMethodChipClass(method) {
      if (!method) return "";
      const m = String(method).toUpperCase();
      if (m.startsWith("CASH")) return "is-teal";
      if (m.startsWith("CC") || m.startsWith("CREDIT CARD")) return "is-cc";
      if (m.startsWith("STORE CREDIT")) return "is-warn";
      if (m.startsWith("FINANCE")) return "is-finance";
      if (m.startsWith("TAX EXEMPT")) return "is-danger";
      return "";
    },
    showPaymentHistory(sale) {
      this.selectedInvoice = { ...sale };
      const paymentMethodsToUse = sale.all_payment_methods || sale.payment_methods;
      if (paymentMethodsToUse && Array.isArray(paymentMethodsToUse) && paymentMethodsToUse.length > 0) {
        this.selectedInvoice.payment_details = paymentMethodsToUse.map(payment => ({
          ts: payment.ts,
          value: payment.value,
          amount: payment.amount,
          name: payment.name,
          store_credit_invoice_id: payment.store_credit_invoice_id,
          charged_tax_itself: payment.charged_tax_itself,
          lease_id: payment.lease_id,
          misc_info: payment.misc_info,
        }));
      } else {
        this.selectedInvoice.payment_details = [];
      }
      this.$bvModal.show('payment-history-modal');
    },
    formatPaymentDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return moment(dateString).format('MMM DD, YYYY hh:mm A');
      } catch (e) { return dateString; }
    },
    getPaymentMethodsDisplay(paymentMethods) {
      if (!paymentMethods || paymentMethods.length === 0) return [];
      if (paymentMethods.length > 0 && typeof paymentMethods[0] === 'object') {
        const methodMap = {
          'cash': 'Cash',
          'cc': 'CC',
          'store_credit': 'Store credit',
          'finance': 'Finance',
          'tax_exempt': 'Tax exempt',
        };
        return [...new Set(
          paymentMethods
            .filter(m => m && m.value)
            .map(m => {
              const base = methodMap[m.value] || m.value.toUpperCase();
              if (m.value === 'finance' && m.name && m.name !== 'N/A') return `${base} · ${m.name}`;
              if (m.value === 'tax_exempt' && m.name && m.name !== 'N/A') return `${base} · ${m.name}`;
              return base;
            })
        )];
      }
      return [];
    },
    getPaymentMethodIcon(method) {
      const iconMap = {
        'cash': '💵',
        'cc': '💳',
        'store_credit': '📋',
        'finance': '🏦',
        'tax_exempt': '🧾',
      };
      return iconMap[method] || '💰';
    },
    isRecentPayment(dateString) {
      if (!dateString) return false;
      try {
        return moment().diff(moment(dateString), 'days') <= 7;
      } catch (e) { return false; }
    },
    navigateToSelectedInvoice() {
      if (this.selectedInvoice) {
        this.$bvModal.hide('payment-history-modal');
        this.$router.push("/sales/viewsale/" + this.selectedInvoice.invoice_id + "/");
      }
    },

    /* ===========================
       LOCATIONS / CHUNKS
    =========================== */
    getFilteredLocations() {
      const excludedLocations = ["101 WH", "FOLCROFT WH", "9TH ST WH"];
      if (!this.packet.locations) return [];
      return this.packet.locations.filter(location => {
        if (this.packet.userDetails.properties.location_id) {
          return location.value == this.packet.userDetails.properties.location_id;
        }
        return !excludedLocations.includes(location.text.toUpperCase());
      });
    },
    row_part_of_chunk(idx) {
      let status = false;
      if (this.chunk_allowed) {
        let chunk = this.chunkoptions.filter(d => d.value == this.chunk_allowed);
        if (chunk.length > 0) {
          chunk = chunk[0];
          if (idx >= chunk.start && idx <= chunk.end) status = true;
        }
      }
      return status;
    },
    chunk_n(items) {
      this.chunkoptions = [];
      let n = items.length;
      if (n == 0) {
        this.chunk_allowed = 0;
        return;
      }
      let each = this.chunksize;
      if (n <= this.chunksize) each = n;
      for (var i = 0; i < n; i = i + each) {
        this.chunkoptions.push({
          start: i,
          end: i + each,
          value: i + each,
          text: i + " TO " + (i + each),
        });
      }
      this.chunk_allowed = this.chunkoptions[0].value;
    },

    /* ===========================
       TOTALS / FILTERS
    =========================== */
    total() {
      let t = 0.0;
      for (var i = 0; i < this.sales.length; i++) {
        let sale = this.sales[i];
        if (this.allowed(sale)) {
          t += this.safeToNumber(sale.payment_amount || sale.paid_so_far);
        }
      }
      return t;
    },
    allowed(row) {
      let status = true;
      const isArchived = this.isArchived(row);
      if (this.combined_status_filter !== "archived" && isArchived) return false;
      if (this.sale_type_filter === "layaway") {
        if (!row.is_layaway || row.transaction_type === "REFUND") status = false;
      }
      if (!status) return false;
      if (this.orderTypeFilter !== "all") {
        const hasMatchingOrderType = row.items && row.items.some(item => item.order_type === this.orderTypeFilter);
        if (!hasMatchingOrderType) status = false;
      }
      if (!status) return false;
      if (this.combined_status_filter !== "all") {
        const currentStatus = this.getStatusDisplay(row);
        switch (this.combined_status_filter) {
          case "new": if (currentStatus !== "NEW") status = false; break;
          case "open": if (currentStatus !== "OPEN") status = false; break;
          case "processed": if (currentStatus !== "PROCESSED") status = false; break;
          case "backorder": if (currentStatus !== "BACKORDER") status = false; break;
          case "partial": if (currentStatus !== "PARTIAL") status = false; break;
          case "delivered": if (currentStatus !== "DELIVERED") status = false; break;
          case "completed": if (currentStatus !== "COMPLETED") status = false; break;
          case "archived": if (!isArchived) status = false; break;
        }
      }
      if (!status) return false;
      if (this.picked_location_id != null && row.location_id != this.picked_location_id) status = false;
      if (!status) return false;
      if (this.picked_payment_method != null) {
        var pms = row.payment_methods || [];
        var picked = this.picked_payment_method;
        var hasMatch = pms.some(function (payment) {
          if (!payment || !payment.value) return false;
          if (picked === "CASH") return payment.value === "cash";
          if (picked === "CC") return payment.value === "cc";
          if (picked === "STORE_CREDIT") return payment.value === "store_credit";
          if (picked === "FINANCE") return payment.value === "finance";
          if (picked === "TAX_EXEMPT") return payment.value === "tax_exempt";
          if (picked.startsWith("FINANCE - ")) return payment.value === "finance" && payment.name === picked.replace("FINANCE - ", "");
          if (picked.startsWith("TAX_EXEMPT - ")) return payment.value === "tax_exempt" && payment.name === picked.replace("TAX_EXEMPT - ", "");
          return false;
        });
        if (!hasMatch) status = false;
      }
      return status;
    },

    /* ===========================
       SEARCH
    =========================== */
    search_dates(e) {
      if (e) e.preventDefault();
      this.query.invoice_id = null;
      this.fetch_init();
    },
    search_invoice_id(e) {
      if (e) e.preventDefault();
      if (!this.query.invoice_id) {
        this.$bvToast.toast("Please specify a valid invoice id", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        return;
      }
      this.fetch_init();
    },

    /* ===========================
       VOID / FETCH
    =========================== */
    void_invoice(invoice_id) {
      let that = this;
      let s = function (response) {
        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        else {
          that.$bvToast.toast("Invoice voided", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.sales = that.sales.filter(d => d.invoice_id != invoice_id);
        }
      };
      let f = function () {
        that.$bvToast.toast("Error voiding invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };
      Requestor.postSyncP("/api/sales/void_invoice/", { invoice_id }, this, s, f);
    },
    fetch_prereqs() {
      let that = this;
      Requestor.getAsyncP(
        "/api/finance_company/get_all_finance_companies/",
        {},
        this,
        function (response) {
          if (response.data.result) that.finance_companies = response.data.result;
          else that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        },
        function () {
          that.$bvToast.toast("Error loading finance companies list.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        }
      );
    },
    fetch_init() {
      this.startApiLoading("Loading Layaway Sales");
      let that = this;
      let path = "/api/sales/get_sales/";
      let query = JSON.parse(JSON.stringify(this.query));
      if (query.invoice_id) {
        query.invoice_id = query.invoice_id.replace(/^\s+|\s+$/gm, '');
        query.startDate = null;
        query.endDate = null;
      }
      let data = {
        userid: this.packet.userDetails.id,
        query: query,
        status: this.status_filter !== "all" ? this.status_filter : null,
        invoice_type: "layaway",
      };
      if (this.packet.userDetails.properties.location_id) {
        data.location_id = this.packet.userDetails.properties.location_id;
      }
      let s = function (response) {
        if (response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          that.sales = response.data.result;
          if (that.sales) {
            that.sales = that.sales.map(function (d) {
              d.payment_methods = ((d.payment_methods == null) ? [] : d.payment_methods);
              d.completed = !!d.completed;
              d.is_layaway = !!d.is_layaway;
              d.total_payments = d.total_payments || 1;
              d.payment_index = d.payment_index || 0;
              return d;
            });
            that.sales.sort((a, b) => {
              if (a.completed !== b.completed) return a.completed ? 1 : -1;
              if (a.is_layaway !== b.is_layaway) return a.is_layaway ? -1 : 1;
              return 0;
            });
          }
          that.chunk_n(that.sales);
        }
        that.sales_being_loaded = false;
        that.stopApiLoading();
      };
      let f = function () {
        that.$bvToast.toast("Error loading layaway sales.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        that.sales_being_loaded = false;
        that.stopApiLoading();
      };
      this.sales_being_loaded = true;
      Requestor.postSyncP(path, data, this, s, f);
    },
    navigateToInvoice(idx) {
      let sale = this.sales[idx];
      let invoice_id = sale.invoice_id;
      const routeData = this.$router.resolve({ path: `/sales/viewsale/${invoice_id}/` });
      window.open(routeData.href, "_blank", "noopener,noreferrer");
    },
    sortby(key) {
      if (key === 'completed') {
        this.sales.sort((a, b) => {
          if (a.completed === b.completed) return 0;
          if (this.sort_asc) return !a.completed && b.completed ? -1 : 1;
          return a.completed && !b.completed ? -1 : 1;
        });
      } else if (key === 'status_display') {
        this.sales.sort((a, b) => {
          const sA = this.getStatusDisplay(a);
          const sB = this.getStatusDisplay(b);
          return this.sort_asc ? sA.localeCompare(sB) : sB.localeCompare(sA);
        });
      } else {
        if (this.sort_asc) this.sales = Requestor.sort(this.sales, key);
        else this.sales = Requestor.sort_reverse(this.sales, key);
      }
      this.sort_asc = !this.sort_asc;
    },

    /* ===========================
       CSV
    =========================== */
    get_csv_data() {
      let data = [];
      let items = this.sales;
      if (items && items.length > 0) {
        for (var i = 0; i < items.length; i++) {
          let row = items[i];
          if (!this.allowed(row)) continue;
          if (!row.items || !Array.isArray(row.items)) continue;
          for (var j = 0; j < row.items.length; j++) {
            let element = row.items[j];
            if (!element) continue;
            if (element.order_type == 'home_delivery' || element.order_type == 'store_pickup') {
              let k = {};
              k["Bill To Name"] = row.customer_fullname || '';
              k["Email Address"] = row.email || '';
              k["Ship-to Name"] = row.customer_fullname || '';
              k["Ship-to Phone"] = (row.customer_phone || '').replace(/\(/g, "").replace(/\)/g, "");
              k["Ship-to Address Line 1"] = row.address || '';
              k["Ship-to Address Line 2"] = "";
              k["Ship-to City"] = row.city || '';
              k["Stst"] = row.state || '';
              k["St Postal"] = row.zipcode || '';
              k["Order No"] = row.invoice_id || '';
              k["Store"] = this.packet.locsubsdict[row.location_id + "|" + row.sublocation_id] || '';
              k["Tr Cd"] = 0;
              let paymentAmount = 0;
              if (row.is_layaway) paymentAmount = this.safeToNumber(row.paid_so_far);
              else paymentAmount = this.safeToNumber(row.payment_amount || row.grand_total);
              k["Order Total"] = this.formatCurrency(paymentAmount);
              k["Product Description"] = element.product_name || '';
              k["Stk Lc"] = this.packet.locsubsdict[element.locsub] || '';
              k["Product Number"] = element.product_sku || '';
              k["Qty Ord"] = element.quantity || 0;
              k["Qty Rsvd"] = element.quantity || 0;
              k["Case Price"] = 0.0;
              k["Volume"] = element.cubes || 0;
              k["Del Dates"] = element.delivery_date ? moment(element.delivery_date).format("MM/DD/YYYY") : '';
              k["Delivery Type"] = element.order_type || '';
              k["Sale Type"] = row.is_layaway ? "LAYAWAY" : "REGULAR";
              k["Status"] = this.getStatusDisplay(row);
              k["Completed"] = row.completed ? "YES" : "NO";
              let paymentMethod = 'N/A';
              if (row.payment_methods && row.payment_methods.length > 0 && row.payment_methods[0]) {
                paymentMethod = this.getPaymentMethodDisplay(row.payment_methods[0].value);
              }
              k["Payment Method"] = paymentMethod;
              k["Payment Amount"] = this.formatCurrency(paymentAmount);
              k["Payment Date"] = row.datetime || '';
              data.push(k);
            }
          }
        }
      }
      return data;
    },
    get_csv_filename() {
      let now = moment().format("YYYY-MM-DD_HHmmss");
      return `LAYAWAY_SALES_${now}.csv`;
    },
  },
  created: function () {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
    if (this.packet.userDetails) this.fetch_prereqs();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.stopLoadingAnimation();
  },
  mounted: function () {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    const Lightpick = require('lightpick');
    let that = this;
    new Lightpick({
      field: document.getElementById('layawaydates'),
      singleDate: false,
      repick: true,
      onSelect: function (start, end) {
        that.query.startDate = start ? start.startOf('day').format('YYYY-MM-DD') : '';
        that.query.endDate = end ? end.endOf('day').format('YYYY-MM-DD') : '';
      }
    });
    this.fetch_init();
  },
}
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                            */
/* ===================================================== */
.ll-page {
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 17px;
}
.ll-wrap { max-width: 1800px; width: 97%; margin: 0 auto; }
@media (max-width: 767px) {
  .ll-page { padding: 10px 0 40px; font-size: 16px; }
  .ll-wrap { width: 100%; padding: 0 12px; }
}

/* ===================================================== */
/* LOADING OVERLAY                                       */
/* ===================================================== */
.ll-loading-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(34, 31, 28, 0.82);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}
.ll-loading-card {
  background: #fff;
  padding: 36px 50px;
  border-radius: 20px;
  text-align: center;
  border-bottom: 5px solid #EE7A22;
  min-width: 360px;
}
.ll-logo {
  width: 60px; height: 60px; border-radius: 14px;
  background: #EE7A22; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 24px;
}
.ll-logo--lg { width: 72px; height: 72px; font-size: 28px; margin-bottom: 18px; animation: llPulse 1.2s ease-in-out infinite; }
@keyframes llPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.ll-loading-text { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin-bottom: 18px; }
.ll-loading-bar { width: 300px; height: 8px; background: #F1EEE9; border-radius: 4px; overflow: hidden; margin: 0 auto; }
.ll-loading-fill { height: 100%; background: #EE7A22; border-radius: 4px; transition: width 0.2s ease; }
.ll-loading-tip { margin-top: 18px; font-size: 15px; color: #736C63; font-weight: 600; }

.ll-fade-enter-active, .ll-fade-leave-active { transition: opacity 0.25s ease; }
.ll-fade-enter, .ll-fade-leave-to { opacity: 0; }
.ll-fade-up-enter-active { animation: llSlideUp 0.4s ease-out; }
@keyframes llSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
.ll-pop-enter-active { animation: llPopIn 0.28s cubic-bezier(0.34, 1.2, 0.64, 1); }
@keyframes llPopIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }

/* ===================================================== */
/* HERO                                                  */
/* ===================================================== */
.ll-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 14px;
}
.ll-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 22px 30px;
  align-items: center;
}
.ll-hero-label {
  font-size: 15px; color: rgba(255, 255, 255, 0.6);
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em;
}
.ll-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 42px; font-weight: 800;
  line-height: 1.1;
  margin: 4px 0 12px;
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
}
.ll-count-pill {
  display: inline-flex; align-items: center; justify-content: center;
  background: #EE7A22; color: #fff;
  font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 800;
  padding: 5px 16px; border-radius: 999px;
}
.ll-hero-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.ll-tag {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 15px; font-weight: 700;
  padding: 6px 15px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.ll-tag.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.ll-tag.is-teal   { background: rgba(15, 110, 107, 0.24); color: #7FDACF; border-color: rgba(15, 110, 107, 0.5); }
.ll-hero-qr {
  display: flex; align-items: center; gap: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px; padding: 14px 20px;
  color: #FFB06B;
}
.ll-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 17px; color: #fff; }
.ll-qr-sub { font-size: 15px; color: rgba(255, 255, 255, 0.65); }
.ll-hero-actions {
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 12px 30px; background: #34302B;
}
.ll-hero-actions .btn { padding: 9px 18px !important; font-size: 16px !important; }
@media (max-width: 900px) {
  .ll-hero-grid { grid-template-columns: 1fr; gap: 14px; }
  .ll-hero-title { font-size: 32px; }
  .ll-hero-actions { padding: 10px 18px; }
}

/* ===================================================== */
/* BUTTONS                                               */
/* ===================================================== */
.ll-btn-primary.btn,
.ll-btn-ghost.btn,
.ll-btn-ghost-dark.btn {
  border-radius: 10px !important;
  font-weight: 700; font-size: 16.5px;
  padding: 10px 18px;
  display: inline-flex; align-items: center; gap: 8px;
  box-shadow: none !important;
}
.ll-btn-primary.btn {
  background: #EE7A22 !important;
  border: 1.5px solid #EE7A22 !important;
  color: #fff !important;
}
.ll-btn-primary.btn:hover:not(:disabled) {
  background: #D4620F !important;
  border-color: #D4620F !important;
}
.ll-btn-ghost.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
}
.ll-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.ll-btn-ghost-dark.btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #fff !important;
  border: 1.5px solid rgba(255, 255, 255, 0.22) !important;
}
.ll-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.ll-download-btn { text-decoration: none; }
.ll-page .btn:disabled { opacity: 0.5; }

/* ===================================================== */
/* CARDS                                                 */
/* ===================================================== */
.ll-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
}

/* ===================================================== */
/* SINGLE-ROW FILTER STRIP                               */
/* ===================================================== */
.ll-filterstrip { padding: 12px 14px !important; }
.ll-filters-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 3px;
  scrollbar-width: thin;
}
.ll-filters-bar::-webkit-scrollbar { height: 8px; }
.ll-filters-bar::-webkit-scrollbar-thumb { background: #DCD6CC; border-radius: 8px; }

.ll-fb-item { flex: 0 0 auto; min-width: 0; }
.ll-fb-search { flex: 1 1 280px; min-width: 240px; }
.ll-fb-dates  { flex: 0 1 260px; min-width: 220px; }

.ll-fb-inputgroup {
  flex-wrap: nowrap;
  width: 100%;
}
.ll-fb-inputgroup >>> .input-group-text {
  height: 48px;
  padding: 0 14px;
  background: #F1EEE9;
  border: 1.5px solid #E5E1DA;
  border-right: 0;
  border-radius: 10px 0 0 10px !important;
  color: #736C63;
  font-size: 17px;
}
.ll-fb-inputgroup >>> .form-control {
  height: 48px !important;
  font-size: 17px !important;
  font-weight: 600 !important;
  border: 1.5px solid #E5E1DA !important;
  background: #F7F5F2 !important;
  color: #221F1C !important;
  border-radius: 0 !important;
  padding: 0 14px !important;
}
.ll-fb-inputgroup >>> .form-control:focus {
  border-color: #EE7A22 !important;
  background: #fff !important;
  box-shadow: none !important;
}
.ll-fb-inputgroup >>> .input-group-append .btn {
  height: 48px;
  border-radius: 0 10px 10px 0 !important;
  padding: 0 18px !important;
  font-size: 15px !important;
}

.ll-fb-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #F7F5F2;
  border: 1.5px solid #E5E1DA;
  border-radius: 999px;
  padding: 0 16px 0 14px;
  height: 48px;
  flex-shrink: 0;
}
.ll-fb-pill-icon { color: #EE7A22; font-size: 17px; flex-shrink: 0; }
.ll-fb-select {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 16.5px !important;
  font-weight: 700 !important;
  color: #221F1C !important;
  padding: 0 26px 0 4px !important;
  height: 46px !important;
  min-width: 160px;
  background-position: right 6px center !important;
  background-size: 14px !important;
  cursor: pointer;
}
.ll-fb-select:focus { box-shadow: none !important; outline: none !important; }

.ll-fb-btn { padding: 0 18px !important; font-size: 15px !important; }
.ll-fb-reset.btn {
  height: 48px;
  width: 48px;
  padding: 0 !important;
  border-radius: 999px !important;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 17px;
}

@media (max-width: 900px) {
  .ll-filters-bar { flex-wrap: wrap; overflow-x: visible; }
  .ll-fb-search, .ll-fb-dates { flex: 1 1 100%; min-width: 0; }
  .ll-fb-pill { flex: 1 1 calc(50% - 5px); }
  .ll-fb-select { min-width: 0; width: 100%; }
}

/* ===================================================== */
/* STATS                                                 */
/* ===================================================== */
.ll-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.ll-stat {
  display: flex; align-items: center; gap: 14px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 6px solid transparent;
  border-radius: 14px;
  padding: 14px 16px;
}
.ll-stat-icon {
  width: 50px; height: 50px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 22px; flex-shrink: 0;
}
.ll-stat-body { display: flex; flex-direction: column; min-width: 0; }
.ll-stat-value {
  font-family: 'Sora', sans-serif;
  font-size: 26px; font-weight: 800;
  line-height: 1.1; letter-spacing: -0.01em;
}
.ll-stat-label { font-size: 14.5px; color: #736C63; font-weight: 600; margin-top: 2px; }
.ll-stat.is-total   { border-left-color: #D4620F; }
.ll-stat.is-total   .ll-stat-icon { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.ll-stat.is-total   .ll-stat-value { color: #D4620F; }
.ll-stat.is-count   { border-left-color: #0F6E6B; }
.ll-stat.is-count   .ll-stat-icon { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.ll-stat.is-count   .ll-stat-value { color: #0F6E6B; }
.ll-stat.is-open    { border-left-color: #C98A12; }
.ll-stat.is-open    .ll-stat-icon { background: linear-gradient(135deg, #E0A524, #C98A12); }
.ll-stat.is-open    .ll-stat-value { color: #C98A12; }
.ll-stat.is-delivered { border-left-color: #0F6E6B; }
.ll-stat.is-delivered .ll-stat-icon { background: linear-gradient(135deg, #3FB6A8, #0F6E6B); }
.ll-stat.is-delivered .ll-stat-value { color: #0F6E6B; }
@media (max-width: 991px) { .ll-stats { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .ll-stats { grid-template-columns: 1fr; } }

/* ===================================================== */
/* TABLE                                                 */
/* ===================================================== */
.ll-table-card { padding: 0 !important; overflow: hidden; }
.ll-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.ll-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 17px;
  min-width: 1360px;
}
.ll-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #221F1C;
  color: #fff;
  text-align: left;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 18px 14px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  border-bottom: 3px solid #EE7A22;
}
.ll-th:hover { background: #34302B; }
.ll-th--actions { cursor: default; }
.ll-th--actions:hover { background: #221F1C; }
.ll-sort-icon { font-size: 13px; opacity: 0.6; margin-left: 5px; }

.ll-totals-row {
  background: #FFF1E4;
  font-weight: 800;
}
.ll-totals-row td {
  padding: 14px;
  border-bottom: 2px solid #F5CFAE;
}
.ll-totals-label {
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #D4620F;
}
.ll-totals-amt {
  display: flex; align-items: center; gap: 8px;
  color: #D4620F;
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  font-weight: 800;
}

.ll-row {
  animation: llRowIn 0.35s ease-out backwards;
  transition: background 0.15s ease;
}
@keyframes llRowIn {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}
.ll-row td {
  padding: 16px 14px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: top;
  background: #fff;
}
.ll-row:hover td { background: #FAF9F7; }
.ll-row.is-highlight td {
  background: #EAF6F4;
  box-shadow: inset 0 0 0 1px rgba(15, 110, 107, 0.25);
}

.ll-td { color: #221F1C; }
.ll-td--actions { vertical-align: middle; }

/* Invoice cell */
.ll-invoice-cell {
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #F7F5F2;
  border-left: 4px solid #C98A12;
}
.ll-invoice-cell.is-layaway { background: #FFF4DC; border-left-color: #C98A12; }
.ll-invoice-id {
  font-family: 'Sora', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #221F1C;
  word-break: break-all;
}
.ll-invoice-date {
  font-family: 'Menlo', monospace;
  font-size: 14px;
  font-weight: 700;
  color: #736C63;
  white-space: nowrap;
}
.ll-invoice-tags { display: flex; flex-wrap: wrap; gap: 5px; }

.ll-mini-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 4px 10px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.ll-mini-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ll-mini-chip.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ll-mini-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.ll-mini-chip.is-cc     { background: #E8EFF7; color: #234A66; border-color: #BBD5E8; }
.ll-mini-chip.is-finance{ background: #EEE6F9; color: #5E37A3; border-color: #D4C0F0; }

.ll-cust-cell { display: flex; flex-direction: column; gap: 4px; }
.ll-cust-name {
  font-weight: 800;
  font-size: 16px;
  color: #221F1C;
  line-height: 1.3;
}
.ll-cust-line {
  font-size: 14.5px;
  color: #4A4540;
  display: flex; align-items: center; gap: 6px;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ll-cust-line .b-icon { color: #736C63; font-size: 14px; }
.ll-cust-line.is-muted { color: #A59D92; }

.ll-loc-cell { display: flex; flex-direction: column; gap: 5px; }
.ll-loc-primary {
  font-weight: 700;
  font-size: 15px;
  color: #221F1C;
  line-height: 1.35;
  display: flex; align-items: center; gap: 6px;
}
.ll-loc-primary .b-icon { color: #EE7A22; font-size: 14px; flex-shrink: 0; }
.ll-loc-secondary {
  font-size: 14px;
  color: #736C63;
  padding-left: 20px;
}
.ll-agent-chip {
  display: inline-flex; align-items: center; gap: 6px;
  align-self: flex-start;
  margin-top: 4px;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 4px 11px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
}
.ll-agent-chip .b-icon { color: #736C63; font-size: 13px; }

.ll-od-cell { display: flex; flex-direction: column; gap: 8px; }
.ll-order-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.ll-deliv-line { display: flex; flex-wrap: wrap; gap: 5px; }

.ll-mini-btn.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 9px !important;
  font-size: 14px !important;
  font-weight: 700;
  padding: 6px 12px !important;
  display: inline-flex; align-items: center; gap: 6px;
  box-shadow: none !important;
  line-height: 1.2 !important;
}
.ll-mini-btn.btn.is-tiny { font-size: 13px !important; padding: 4px 10px !important; }
.ll-mini-btn.btn:hover { border-color: #EE7A22 !important; color: #D4620F !important; }
.ll-mini-btn.btn.is-primary {
  background: #EE7A22 !important;
  border-color: #EE7A22 !important;
  color: #fff !important;
}
.ll-mini-btn.btn.is-primary:hover {
  background: #D4620F !important;
  border-color: #D4620F !important;
  color: #fff !important;
}
.ll-mini-btn.btn.is-teal {
  background: #0F6E6B !important;
  border-color: #0F6E6B !important;
  color: #fff !important;
}
.ll-mini-btn.btn.is-teal:hover { background: #0B5552 !important; border-color: #0B5552 !important; }
.ll-mini-btn.btn.is-danger { color: #C93C30 !important; }
.ll-mini-btn.btn.is-danger:hover {
  background: #C93C30 !important;
  border-color: #C93C30 !important;
  color: #fff !important;
}

.ll-muted-small {
  font-size: 14px;
  color: #A59D92;
  font-style: italic;
}

.ll-pay-cell {
  display: flex; flex-direction: column; gap: 5px;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  transition: background 0.15s ease;
}
.ll-pay-cell:hover { background: #FFF1E4; }
.ll-pay-amt {
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
}
.ll-pay-amt.is-teal   { color: #0F6E6B; }
.ll-pay-amt.is-danger { color: #C93C30; }
.ll-methods-cell {
  display: flex; flex-wrap: wrap; gap: 4px;
}
.ll-pay-note { font-size: 13.5px; color: #A59D92; }

.ll-status-badge {
  display: inline-flex; flex-direction: column;
  gap: 3px;
  font-family: 'Sora', sans-serif;
  font-size: 14.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 8px 12px;
  border-radius: 9px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  text-transform: uppercase;
  min-width: 110px;
  text-align: center;
}
.ll-status-badge.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ll-status-badge.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ll-status-badge.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.ll-status-sub {
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  opacity: 0.75;
}

.ll-actions {
  display: flex; flex-wrap: wrap; gap: 5px;
  align-items: center;
}

/* ===================================================== */
/* EMPTY / LOADING                                       */
/* ===================================================== */
.ll-empty {
  text-align: center;
  padding: 60px 30px;
  border: 2px dashed #D6CFC4;
  background: #FAF9F7;
}
.ll-empty-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 34px; margin-bottom: 18px;
}
.ll-empty h3 { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; margin: 0 0 8px; }
.ll-empty p { color: #736C63; max-width: 50ch; margin: 0 auto 20px; line-height: 1.6; font-size: 17px; }

.ll-loading-inline {
  display: flex; align-items: center; justify-content: center;
  gap: 12px;
  color: #736C63;
  font-weight: 600;
  font-size: 17px;
  padding: 40px 20px;
}

.ll-empty-inline {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 40px 20px;
  color: #A59D92;
}

/* ===================================================== */
/* MODAL                                                 */
/* ===================================================== */
.ll-modal.modal-content {
  border: 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.ll-modal .modal-header {
  background: #221F1C !important;
  color: #fff !important;
  border: 0 !important;
  border-bottom: 4px solid #EE7A22 !important;
  padding: 18px 26px !important;
}
.ll-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.ll-modal .modal-body { background: #F6F4F0; padding: 22px 24px; }

.ll-modal-title { display: flex; align-items: center; gap: 16px; }
.ll-modal-title-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(238, 122, 34, 0.2);
  color: #FFB06B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 20px;
}
.ll-modal-title-main { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; }
.ll-modal-title-sub { font-size: 15px; color: rgba(255, 255, 255, 0.65); }

.ll-modal-body { display: flex; flex-direction: column; gap: 16px; font-size: 17px; }
.ll-modal-summary {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; flex-wrap: wrap;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  padding: 16px 20px;
}
.ll-modal-summary strong { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 700; }
.ll-modal-summary small { display: block; color: #736C63; font-size: 15px; margin-top: 3px; }

.ll-modal-foot {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid #E5E1DA;
}
.ll-foot-note { font-size: 15px; color: #736C63; font-weight: 600; }
.ll-foot-note b { color: #221F1C; }
.ll-modal-foot-actions { display: flex; gap: 10px; flex-wrap: wrap; }

.ll-date-list,
.ll-order-list {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 10px;
  max-height: 400px; overflow-y: auto;
}
.ll-date-item,
.ll-order-item {
  display: flex; align-items: center; gap: 16px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 4px solid #EE7A22;
  border-radius: 12px;
  padding: 14px 18px;
}
.ll-date-item:hover,
.ll-order-item:hover { box-shadow: 0 6px 16px -10px rgba(34, 31, 28, 0.25); }
.ll-date-item.is-upcoming { border-left-color: #0F6E6B; }
.ll-date-icon,
.ll-order-icon {
  width: 44px; height: 44px; border-radius: 10px;
  background: #FFF1E4;
  color: #D4620F;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.ll-order-item.is-home-delivery  { border-left-color: #0F6E6B; }
.ll-order-item.is-store-pickup   { border-left-color: #C98A12; }
.ll-order-item.is-curbside       { border-left-color: #EE7A22; }
.ll-order-item.is-delivery       { border-left-color: #234A66; }
.ll-order-item.is-pickup         { border-left-color: #5E37A3; }
.ll-date-details, .ll-order-details { flex: 1 1 auto; min-width: 0; }
.ll-date-display, .ll-order-display { font-weight: 700; font-size: 17px; color: #221F1C; }
.ll-date-status, .ll-order-count { font-size: 15px; font-weight: 600; margin-top: 3px; }
.ll-date-status.is-ok    { color: #0F6E6B; }
.ll-date-status.is-warn  { color: #C98A12; }
.ll-date-status.is-teal  { color: #2B83B5; }
.ll-date-status.is-muted { color: #736C63; }

.ll-pay-modal-head {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 16px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  padding: 18px 20px;
}
.ll-pay-modal-customer strong { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; display: block; }
.ll-pay-modal-customer small { display: block; color: #736C63; font-size: 15px; margin-top: 4px; }
.ll-pay-modal-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 10px; }
.ll-pay-modal-totals { display: flex; flex-direction: column; gap: 6px; }
.ll-pay-tot-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 10px;
  font-size: 16px;
  padding: 7px 0;
  border-bottom: 1px dashed #E5E1DA;
}
.ll-pay-tot-row:last-child { border-bottom: 0; }
.ll-pay-tot-row span { color: #736C63; font-weight: 600; }
.ll-pay-tot-row strong { font-family: 'Sora', sans-serif; font-size: 21px; font-weight: 800; }
.ll-pay-tot-row.is-teal strong { color: #0F6E6B; }
.ll-pay-tot-row.is-danger strong { color: #C93C30; }

.ll-pay-progress {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  padding: 18px 20px;
}
.ll-pay-progress-head {
  display: flex; justify-content: space-between;
  font-size: 15px; font-weight: 600;
  color: #736C63; margin-bottom: 10px;
}
.ll-pay-progress-head strong { color: #221F1C; font-size: 17px; }
.ll-pay-progress-foot {
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px;
  margin-top: 10px; font-size: 14.5px; font-weight: 700;
}
.ll-pay-progress-foot .is-warn { color: #8A5E09; }

.ll-pay-section {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  overflow: hidden;
}
.ll-pay-section-head {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 18px;
  background: #221F1C;
  color: #fff;
  font-weight: 700; font-size: 17px;
}
.ll-pay-section-head .b-icon { color: #FFB06B; font-size: 18px; }
.ll-pay-section-head .ll-chip {
  background: rgba(255, 255, 255, 0.12);
  color: #fff; border-color: rgba(255, 255, 255, 0.2);
  margin-left: auto;
}
.ll-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 14px; font-weight: 700;
  padding: 5px 13px; border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.ll-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ll-chip.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ll-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }

.ll-pay-list { list-style: none; margin: 0; padding: 0; }
.ll-pay-item {
  display: flex; align-items: center; gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #F1EEE9;
}
.ll-pay-item:last-child { border-bottom: 0; }
.ll-pay-item.is-recent { background: #FFFBEA; }
.ll-pay-icon {
  width: 48px; height: 48px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
  background: #F1EEE9;
}
.ll-pay-icon.is-cash         { background: #E2F2F0; }
.ll-pay-icon.is-cc           { background: #E8EFF7; }
.ll-pay-icon.is-store_credit { background: #FFF4DC; }
.ll-pay-icon.is-finance      { background: #EEE6F9; }
.ll-pay-icon.is-tax_exempt   { background: #FBE9E7; }
.ll-pay-item-body { flex: 1 1 auto; min-width: 0; }
.ll-pay-item-method { font-weight: 700; font-size: 17px; color: #221F1C; }
.ll-pay-item-date { font-size: 14.5px; color: #736C63; margin-top: 3px; }
.ll-pay-item-tags { display: flex; gap: 6px; margin-top: 7px; flex-wrap: wrap; }
.ll-pay-item-amount {
  font-family: 'Sora', sans-serif;
  font-size: 21px; font-weight: 800;
  text-align: right;
  white-space: nowrap;
}
.ll-pay-item-amount.is-teal { color: #0F6E6B; }
.ll-pay-item-amount.is-danger { color: #C93C30; }

@media (max-width: 767px) {
  .ll-pay-modal-head { grid-template-columns: 1fr; }
}

/* ===================================================== */
/* REDUCED MOTION                                        */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
  .ll-row, .ll-logo--lg, .ll-count-pill { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/ListLayAwaySales.vue